// Previsões abatidas — Alimentação em campo e Hospedagem (19/10/2026).
//
// Pedido do Lucas: como a de combustível, a previsão do mês diminui conforme entram
// as despesas reais da linha e vai a zero quando o mês fecha. A conta mora no banco
// (gatilho em toda gravação do Contas a Pagar, dos lançamentos manuais e dos
// diferidos — docs/previsoes-automaticas.md). Esta aba mostra, refaz a conta ao abrir
// (conserta previsão que ficou para trás) e deixa o administrador ajustar o valor
// mensal das previsões que o sistema cria. O combustível segue na aba própria.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, App as AntApp, Button, Card, InputNumber, Modal, Space, Spin, Table, Typography } from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';

const { Text } = Typography;

interface Previsao {
  id: string;
  periodo: string;
  descricao: string;
  previsto: number | null;
  realizado: number;
  vale: number | null;
  acompanhada: boolean;
}
interface Linha {
  chave: string;
  linha_dre: string;
  rotulo: string;
  valor_mensal: number;
  desde: string;
  cria_sozinha: boolean;
  alterado_por: string | null;
  alterado_em: string | null;
  realizado_mes: number;
  previsoes: Previsao[];
}
interface Painel { mes: string; pode_ajustar: boolean; linhas: Linha[] }

const brl = (v: number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesAno = (periodo?: string) => (periodo ? `${MESES[Number(periodo.slice(5, 7)) - 1] || ''}/${periodo.slice(0, 4)}` : '');
const corpoDe = (d: any) => (d && typeof d.body === 'string' ? JSON.parse(d.body) : d);
const erroDe = (e: any, padrao: string) => {
  const d = corpoDe(e?.response?.data);
  return (d && (d.error || d.erro)) || padrao;
};

function FinanceiroPrevisoesInner() {
  const { message } = AntApp.useApp();
  const [painel, setPainel] = useState<Painel | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [ajuste, setAjuste] = useState<Linha | null>(null);
  const [valor, setValor] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const token = await getToken();
      // Refaz a conta antes de ler (quem não edita o Contas a Pagar só lê).
      await axios.patch(`${API_URL}/contas-pagar/previsoes-automaticas`, { refazer: true },
        { headers: { Authorization: token } }).catch(() => null);
      const r = await axios.get(`${API_URL}/contas-pagar`, {
        headers: { Authorization: token }, params: { visao: 'previsoes-automaticas' },
      });
      const dados = corpoDe(r.data);
      // Tela publicada antes do servidor novo: a rota antiga devolve a lista do Contas a Pagar.
      if (!dados || !Array.isArray(dados.linhas)) {
        setPainel(null);
        setErro('A aba ainda está sendo publicada. Tente de novo em alguns minutos.');
        return;
      }
      setPainel(dados);
      setErro(null);
    } catch (e: any) {
      setErro(erroDe(e, 'Não foi possível carregar as previsões.'));
    } finally {
      setCarregando(false);
    }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const abrirAjuste = (l: Linha) => { setAjuste(l); setValor(l.valor_mensal); };
  const salvar = async () => {
    if (!ajuste) return;
    if (valor == null || valor < 0) { message.error('Informe o valor mensal (zero ou maior).'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.patch(`${API_URL}/contas-pagar/previsoes-automaticas`, { chave: ajuste.chave, valor },
        { headers: { Authorization: token } });
      const d = corpoDe(r.data) || {};
      message.success(d.sem_mudanca ? 'O valor mensal não mudou.' : `Valor mensal de ${ajuste.linha_dre} salvo.`);
      setAjuste(null);
      carregar();
    } catch (e: any) {
      message.error(erroDe(e, 'Não foi possível salvar o valor mensal.'), 8);
    } finally {
      setSalvando(false);
    }
  };

  const colunas = [
    { title: 'Mês', dataIndex: 'periodo', key: 'periodo', width: 110, render: (p: string) => mesAno(p) },
    { title: 'Previsto', dataIndex: 'previsto', key: 'previsto', align: 'right' as const, render: (v: number) => brl(v) },
    { title: 'Já lançado no mês', dataIndex: 'realizado', key: 'realizado', align: 'right' as const, render: (v: number) => brl(v) },
    { title: 'A previsão vale agora', dataIndex: 'vale', key: 'vale', align: 'right' as const,
      render: (v: number) => <Text strong>{brl(v)}</Text> },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <Text type="secondary">
          A previsão do mês diminui sozinha conforme entram as despesas da linha. Quando o mês fecha, ela vai a zero
          e ficam só os lançamentos reais. O combustível está na aba ⛽ Combustíveis.
        </Text>
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={carregando}>Atualizar</Button>
      </Space>
      {erro && <Alert type="error" showIcon message={erro} />}
      {!painel && carregando && <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', gap: 16 }}>
        {(painel?.linhas || []).map((l) => {
          const temDoMes = l.previsoes.some((p) => p.periodo === painel?.mes);
          return (
            <Card key={l.chave} title={l.linha_dre} size="small" loading={!painel && carregando}
              extra={painel?.pode_ajustar
                ? <Button size="small" icon={<EditOutlined />} onClick={() => abrirAjuste(l)}>Ajustar valor mensal</Button>
                : null}>
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <Table rowKey="id" size="small" pagination={false} dataSource={l.previsoes} columns={colunas}
                  scroll={{ x: 520 }} locale={{ emptyText: 'Nenhuma previsão em aberto' }} />
                {!temDoMes && (
                  <Text type="secondary">
                    Sem previsão em {mesAno(painel?.mes)}. Já lançado no mês: {brl(l.realizado_mes)}.
                  </Text>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  A partir de {mesAno(l.desde)}, o sistema cria a previsão de cada mês com {brl(l.valor_mensal)}.
                  {l.alterado_por && !String(l.alterado_por).startsWith('migration') ? ` Valor ajustado por ${l.alterado_por}.` : ''}
                </Text>
              </Space>
            </Card>
          );
        })}
      </div>

      <Modal
        open={!!ajuste} title={ajuste ? `Valor mensal — ${ajuste.linha_dre}` : ''}
        okText="Salvar" cancelText="Cancelar" confirmLoading={salvando} onOk={salvar}
        onCancel={() => setAjuste(null)} width="94vw" style={{ maxWidth: 560, top: 16 }} destroyOnHidden
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            Quanto se espera gastar por mês nesta linha. A partir de {mesAno(ajuste?.desde)}, o sistema cria a previsão
            de cada mês com este valor. Ela diminui com o que for lançado no mês e vai a zero quando o mês fecha.
            As previsões do Conta Azul até dezembro ficam com o valor delas. Para mudar uma delas, edite o lançamento
            no Contas a Pagar.
          </Text>
          <InputNumber {...PROPS_DINHEIRO_BR} min={0} style={{ width: '100%' }} value={valor ?? undefined}
            onChange={(v) => setValor(v == null ? null : Number(v))} autoFocus addonBefore="R$" />
        </Space>
      </Modal>
    </Space>
  );
}

export default function FinanceiroPrevisoes() {
  return (
    <AntApp>
      <FinanceiroPrevisoesInner />
    </AntApp>
  );
}
